import { useState, useEffect } from 'react';
import { useWebRTC } from '../../hooks/useWebRTC';
import CallModal from './CallModal';

interface CallButtonProps {
  className?: string;
  children?: React.ReactNode;
}

export default function CallButton({ className, children }: CallButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [callerName, setCallerName] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const {
    callState,
    error,
    localStream,
    remoteStream,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    startCall,
    endCall,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    checkAvailability,
  } = useWebRTC();

  useEffect(() => {
    checkAvailability().then(setIsAvailable);
    // Check availability every 30 seconds
    const interval = setInterval(() => {
      checkAvailability().then(setIsAvailable);
    }, 30000);
    return () => clearInterval(interval);
  }, [checkAvailability]);

  const handleStartCall = async () => {
    if (!captchaToken) {
      // In dev mode, use a placeholder
      if (import.meta.env.DEV) {
        await startCall('dev-token', callerName || 'Anonymous');
      }
      return;
    }
    await startCall(captchaToken, callerName || 'Anonymous');
  };

  const handleClose = () => {
    if (callState !== 'idle' && callState !== 'ended' && callState !== 'error') {
      endCall();
    }
    setShowModal(false);
    setCaptchaToken(null);
    setCallerName('');
  };

  const isInCall = callState === 'ringing' || callState === 'connecting' || callState === 'connected';

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={className}
        disabled={!isAvailable && !import.meta.env.DEV}
        title={isAvailable ? 'Start a call' : 'Not available right now'}
      >
        {children || (
          <>
            <PhoneIcon />
            {isAvailable ? 'Call Me' : 'Unavailable'}
          </>
        )}
      </button>

      {showModal && (
        <CallModal
          isOpen={showModal}
          onClose={handleClose}
          callState={callState}
          error={error}
          localStream={localStream}
          remoteStream={remoteStream}
          isAudioEnabled={isAudioEnabled}
          isVideoEnabled={isVideoEnabled}
          isScreenSharing={isScreenSharing}
          callerName={callerName}
          onCallerNameChange={setCallerName}
          onCaptchaVerify={setCaptchaToken}
          onStartCall={handleStartCall}
          onEndCall={endCall}
          onToggleAudio={toggleAudio}
          onToggleVideo={toggleVideo}
          onToggleScreenShare={toggleScreenShare}
          isInCall={isInCall}
          captchaVerified={!!captchaToken || import.meta.env.DEV}
        />
      )}
    </>
  );
}

function PhoneIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}
